import type {
  CodeClientConfig,
  CodeResponse,
  NonOAuthError,
  OverridableTokenClientConfig,
  TokenClientConfig,
  TokenResponse,
} from '../types/types';

import { useCallback, useEffect, useRef } from 'react';
import { useLoadGoogleScript } from './useLoadGoogleScript';

interface ImplicitFlowOptions extends Omit<TokenClientConfig, 'client_id' | 'scope' | 'callback'> {
  client_id?: string;
  onSuccess?: (
    tokenResponse: Omit<TokenResponse, 'error' | 'error_description' | 'error_uri'>,
  ) => void;
  onError?: (
    errorResponse: Pick<TokenResponse, 'error' | 'error_description' | 'error_uri'>,
  ) => void;
  onNonOAuthError?: (nonOAuthError: NonOAuthError) => void;
  scope?: TokenClientConfig['scope'];
  overrideScope?: boolean;
}

interface AuthCodeFlowOptions extends Omit<CodeClientConfig, 'client_id' | 'scope' | 'callback'> {
  client_id?: string;
  onSuccess?: (
    codeResponse: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>,
  ) => void;
  onError?: (
    errorResponse: Pick<CodeResponse, 'error' | 'error_description' | 'error_uri'>,
  ) => void;
  onNonOAuthError?: (nonOAuthError: NonOAuthError) => void;
  scope?: CodeResponse['scope'];
  overrideScope?: boolean;
}

export type UseGoogleLoginOptionsImplicitFlow = {
  flow?: 'implicit';
} & ImplicitFlowOptions;

export type UseGoogleLoginOptionsAuthCodeFlow = {
  flow?: 'auth-code';
} & AuthCodeFlowOptions;

export type UseGoogleLoginOptions =
  | UseGoogleLoginOptionsImplicitFlow
  | UseGoogleLoginOptionsAuthCodeFlow;

/**
 * Google OAuth Hook - 纯粹的 Google 认证功能
 *
 * 这是一个底层 Hook，只负责 Google OAuth 认证流程，不包含业务逻辑。
 * 业务逻辑（如登录、注册）应该在调用方处理。
 *
 * @note 需要在模板中引入 Google SDK
 * @see https://github.com/MomenSherif/react-oauth#custom-login-button-implicit--authorization-code-flow
 * @see https://developers.google.com/identity/gsi/web/guides/overview
 */
export function useGoogleLogin(
  options: UseGoogleLoginOptionsImplicitFlow,
): (overrideConfig?: OverridableTokenClientConfig) => void;
export function useGoogleLogin(options: UseGoogleLoginOptionsAuthCodeFlow): () => void;
export function useGoogleLogin({
  flow = 'implicit',
  scope = '',
  onSuccess,
  onError,
  onNonOAuthError,
  overrideScope,
  state,
  client_id,
  ...props
}: UseGoogleLoginOptions): unknown {
  const clientRef = useRef<any>(null);

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const onNonOAuthErrorRef = useRef(onNonOAuthError);
  onNonOAuthErrorRef.current = onNonOAuthError;

  const { loaded } = useLoadGoogleScript();

  useEffect(() => {
    if (!loaded) {
      return;
    }
    if (!client_id) {
      console.warn('[useGoogleLogin] client_id is not provided, Google login will not work');
      return;
    }
    if (!window?.google?.accounts?.oauth2) {
      return;
    }
    const clientMethod = flow === 'implicit' ? 'initTokenClient' : 'initCodeClient';

    const client = window.google.accounts.oauth2[clientMethod]({
      client_id,
      redirect_uri: 'postmessage',
      scope: overrideScope ? scope : `openid profile email ${scope}`,
      callback: (response: TokenResponse | CodeResponse) => {
        if (response.error) {
          return onErrorRef.current?.(response);
        }
        onSuccessRef.current?.(response as any);
      },
      error_callback: (nonOAuthError: NonOAuthError) => {
        onNonOAuthErrorRef.current?.(nonOAuthError);
      },
      state,
      ...props,
    });

    clientRef.current = client;
  }, [flow, scope, state, loaded, client_id]);

  const loginImplicitFlow = useCallback((overrideConfig?: OverridableTokenClientConfig) => {
    clientRef.current?.requestAccessToken(overrideConfig);
  }, []);

  const loginAuthCodeFlow = useCallback(() => {
    clientRef.current?.requestCode();
  }, []);

  return flow === 'implicit' ? loginImplicitFlow : loginAuthCodeFlow;
}
